// Full-screen HUD editor (launched from Settings → HUD).
//
// It edits the layout document only: the live HUD elements stay in place (above
// a dark editing backdrop), a transparent hit layer captures drags, and the
// property panel writes the document. The runtime applies changes live.

import {
  DEFAULT_ANCHORS,
  DEFAULT_BANDS,
  HUD_ELEMENTS,
  HUD_ELEMENT_BY_ID,
  CUSTOM_KIND_LABELS,
  SOURCE_LABELS,
  clamp,
  clone,
  defaultCustom,
  defaultElement,
  defaultStore,
  elementSource,
  isHex,
  normalizeStore,
  sourceMax,
  type HudAnchor,
  type HudCustomKind,
  type HudCustomSpec,
  type HudDocument,
  type HudElementLayout,
  type HudLayoutStore,
  type HudSource,
  type HudValuePaint,
} from "../hud/types";
import { hudStageSize } from "../stage";
import { dsElement } from "./daisy";

export interface HudEditorCallbacks {
  /** Apply without persisting (drag / slider). */
  onLive: (store: HudLayoutStore, doc: HudDocument) => void;
  /** Apply and persist. */
  onCommit: (store: HudLayoutStore, doc: HudDocument) => void;
  onTestValue: (source: "health" | "armor" | "ammo" | "speed", value: number) => void;
  onOpen: (open: boolean) => void;
}

const ANCHOR_LABELS: { value: HudAnchor; label: string }[] = [
  { value: "auto", label: "Auto (shipped position)" },
  { value: "top-left", label: "Top left" },
  { value: "top", label: "Top" },
  { value: "top-right", label: "Top right" },
  { value: "left", label: "Left" },
  { value: "center", label: "Center" },
  { value: "right", label: "Right" },
  { value: "bottom-left", label: "Bottom left" },
  { value: "bottom", label: "Bottom" },
  { value: "bottom-right", label: "Bottom right" },
];

const PREVIEW: Record<string, string> = {
  timer: "0:00.00",
  "checkpoint-hud": "CP 3/12 · #2 · PB -0.069s",
  "hud-weapon": "Rocket Launcher",
  "hud-ammo": "50",
  "hud-health": "100",
  "hud-armor": "0",
  coords: "pos 0.0 0.0 0.0",
  "recall-hud": "cp 0/0",
  "vote-hud": "vote 1/2",
  "spectate-hud": "spectating",
  announce: "ANNOUNCEMENT",
};

const PRESET_KEY = "webrace.hud.presets.v1";

interface HudPreset {
  name: string;
  store: HudLayoutStore;
}

function readPresets(): HudPreset[] {
  try {
    const raw = JSON.parse(localStorage.getItem(PRESET_KEY) ?? "[]");
    if (!Array.isArray(raw)) return [];
    const out: HudPreset[] = [];
    for (const item of raw.slice(0, 30)) {
      if (!item || typeof item !== "object") continue;
      const name = typeof item.name === "string" ? item.name.trim().slice(0, 32) : "";
      if (!name) continue;
      out.push({ name, store: normalizeStore(item.store) });
    }
    return out;
  } catch {
    return [];
  }
}

function writePresets(list: HudPreset[]): void {
  try {
    localStorage.setItem(PRESET_KEY, JSON.stringify(list.slice(-30)));
  } catch {
    /* quota/private mode: presets stay in memory for this session only */
  }
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

function hPart(a: HudAnchor): "left" | "center" | "right" {
  return a.includes("left") ? "left" : a.includes("right") ? "right" : "center";
}
function vPart(a: HudAnchor): "top" | "middle" | "bottom" {
  return a.includes("top") ? "top" : a.includes("bottom") ? "bottom" : "middle";
}

/** Nearest of the 9 pivots for a rect (used when the first drag happens). */
function nearestAnchor(rect: DOMRect): HudAnchor {
  const { w: vw, h: vh } = hudStageSize();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const h = cx < vw / 3 ? "left" : cx > (vw * 2) / 3 ? "right" : "center";
  const v = cy < vh / 3 ? "top" : cy > (vh * 2) / 3 ? "bottom" : "middle";
  if (v === "top") return h === "left" ? "top-left" : h === "right" ? "top-right" : "top";
  if (v === "bottom") return h === "left" ? "bottom-left" : h === "right" ? "bottom-right" : "bottom";
  return h === "left" ? "left" : h === "right" ? "right" : "center";
}

/** Anchor-relative offsets that place a rect at its current spot. */
function offsetsFromRect(rect: DOMRect, anchor: HudAnchor): { x: number; y: number } {
  const { w: vw, h: vh } = hudStageSize();
  const h = hPart(anchor);
  const v = vPart(anchor);
  const px = h === "left" ? rect.left : h === "right" ? rect.right : rect.left + rect.width / 2;
  const py = v === "top" ? rect.top : v === "bottom" ? rect.bottom : rect.top + rect.height / 2;
  return {
    x: Math.round(h === "left" ? px : h === "right" ? vw - px : px - vw / 2),
    y: Math.round(v === "top" ? py : v === "bottom" ? vh - py : py - vh / 2),
  };
}

export class HudEditor {
  private store: HudLayoutStore;
  private docKind: "playing" | "spectate" = "playing";
  private selected: string | null = null;
  private root: HTMLElement;
  private boxesEl!: HTMLElement;
  private hitEl!: HTMLElement;
  private listEl!: HTMLElement;
  private propsEl!: HTMLElement;
  private testEl!: HTMLElement;
  private boxMap = new Map<string, HTMLElement>();
  private open = false;
  private drag: {
    id: string;
    startX: number;
    startY: number;
    rect: DOMRect;
    anchor: HudAnchor;
    /** The layout had no explicit anchor before this drag. */
    wasAuto: boolean;
  } | null = null;
  private previewOriginals = new Map<string, string>();
  private commitTimer = 0;
  private testValues = { health: 100, armor: 0, ammo: 50, speed: 320 };
  private presetNameEl!: HTMLInputElement;
  private presetSelEl!: HTMLSelectElement;

  constructor(
    private cb: HudEditorCallbacks,
    store: HudLayoutStore,
  ) {
    this.store = clone(store);
    this.root = el("div");
    this.root.id = "hud-editor";
    this.build();
    document.body.append(this.root);
  }

  setStore(store: HudLayoutStore): void {
    if (this.open) return; // the editor is the source of truth while open
    this.store = clone(store);
  }

  isOpen(): boolean {
    return this.open;
  }

  openEditor(): void {
    if (this.open) return;
    this.open = true;
    document.body.classList.add("hud-editing");
    this.root.classList.add("open");
    this.previewOriginals.clear();
    this.applyPreviewText();
    this.buildElements();
    this.buildProps();
    this.buildTestValues();
    for (const [source, value] of Object.entries(this.testValues) as ["health" | "armor" | "ammo" | "speed", number][]) {
      this.cb.onTestValue(source, value);
    }
    this.selected = null;
    this.syncDoc();
    this.cb.onOpen(true);
    this.loop();
  }

  close(): void {
    if (!this.open) return;
    if (this.commitTimer) {
      clearTimeout(this.commitTimer);
      this.commitTimer = 0;
      this.commit();
    }
    this.open = false;
    this.drag = null;
    document.body.classList.remove("hud-editing");
    this.root.classList.remove("open");
    this.restorePreviewText();
    this.cb.onOpen(false);
  }

  private build(): void {
    this.boxesEl = el("div");
    this.boxesEl.id = "hud-editor-boxes";
    this.hitEl = el("div");
    this.hitEl.id = "hud-editor-hit";
    this.hitEl.addEventListener("pointerdown", (e) => this.onPointerDown(e));

    const toolbar = el("div");
    toolbar.id = "hud-ed-editor-toolbar";
    toolbar.className = "hud-ed-toolbar";
    toolbar.append(el("strong", "", "HUD EDITOR"));
    const docSel = el("select");
    for (const [value, label] of [
      ["playing", "Playing HUD"],
      ["spectate", "Spectating HUD"],
    ] as [string, string][]) {
      const o = el("option", "", label);
      o.value = value;
      docSel.append(o);
    }
    docSel.value = this.docKind;
    docSel.addEventListener("change", () => {
      this.docKind = docSel.value as "playing" | "spectate";
      this.selected = null;
      this.buildProps();
      this.syncDoc();
    });
    const reset = el("button", "xhair-btn", "Reset HUD");
    dsElement(reset, "button", { style: "outline", size: "sm" });
    reset.title = "Reset this HUD to the shipped defaults (layout, visibility and removed elements)";
    reset.addEventListener("click", () => {
      if (this.docKind === "playing") this.store.playing = defaultStore().playing;
      else this.store.spectate = defaultStore().spectate;
      this.selected = null;
      this.buildElements();
      this.buildProps();
      this.commit();
    });
    const close = el("button", "xhair-btn", "Close (Esc)");
    dsElement(close, "button", { style: "outline", size: "sm" });
    close.addEventListener("click", () => this.close());
    toolbar.append(docSel, reset, close);

    const panel = el("aside");
    panel.id = "hud-editor-panel";
    panel.className = "hud-ed-panel";
    this.listEl = el("div");
    this.listEl.className = "hud-ed-elements";
    this.propsEl = el("div");
    this.propsEl.className = "hud-ed-props";
    this.testEl = el("div");
    this.testEl.className = "hud-ed-test";
    panel.append(this.listEl, this.propsEl, this.testEl);

    panel.insertBefore(toolbar, this.listEl);

    const presets = el("div");
    presets.className = "hud-ed-presets";
    this.presetNameEl = el("input") as HTMLInputElement;
    this.presetNameEl.type = "text";
    this.presetNameEl.placeholder = "Preset name";
    this.presetNameEl.spellcheck = false;
    this.presetSelEl = el("select") as HTMLSelectElement;
    this.presetSelEl.title = "Saved HUD presets";
    const savePreset = el("button", "xhair-btn", "Save");
    dsElement(savePreset, "button", { style: "outline", size: "xs" });
    savePreset.title = "Save the whole HUD (playing + spectating) under this name";
    savePreset.addEventListener("click", () => this.savePreset());
    const loadPreset = el("button", "xhair-btn", "Load");
    dsElement(loadPreset, "button", { style: "outline", size: "xs" });
    loadPreset.addEventListener("click", () => this.loadPreset());
    const deletePreset = el("button", "xhair-btn", "Delete");
    dsElement(deletePreset, "button", { style: "outline", color: "error", size: "xs" });
    deletePreset.addEventListener("click", () => this.deletePreset());
    presets.append(this.presetNameEl, savePreset, this.presetSelEl, loadPreset, deletePreset);
    panel.insertBefore(presets, this.listEl);
    this.refreshPresets();
    this.root.append(this.boxesEl, this.hitEl, panel);

    window.addEventListener(
      "keydown",
      (e) => {
        if (!this.open) return;
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          this.close();
          return;
        }
        // Arrow keys nudge the selected element (Shift = grid step).
        const tag = e.target instanceof HTMLElement ? e.target.tagName : "";
        if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
        if (!this.selected) return;
        const step = e.shiftKey ? 8 : 1;
        let dx = 0;
        let dy = 0;
        if (e.key === "ArrowLeft") dx = -step;
        else if (e.key === "ArrowRight") dx = step;
        else if (e.key === "ArrowUp") dy = -step;
        else if (e.key === "ArrowDown") dy = step;
        if (dx === 0 && dy === 0) return;
        e.preventDefault();
        e.stopPropagation();
        this.nudge(dx, dy);
      },
      true,
    );
    window.addEventListener("resize", () => {
      if (this.open) this.buildProps();
    });
  }

  private doc(): HudDocument {
    return this.docKind === "playing" ? this.store.playing : this.store.spectate;
  }

  private element(id: string): HudElementLayout | undefined {
    return this.doc().elements.find((e) => e.id === id);
  }

  /** Built-ins + custom value elements, in list order. */
  private items(): { id: string; label: string; group: string; custom: HudCustomSpec | null }[] {
    const builtins = HUD_ELEMENTS.filter((d) => this.element(d.id)?.removed !== true).map((d) => ({
      id: d.id,
      label: d.label,
      group: d.group as string,
      custom: null,
    }));
    const customs = this.doc()
      .elements.filter((e) => e.custom)
      .map((e) => ({
        id: e.id,
        label: `${CUSTOM_KIND_LABELS[e.custom!.kind]}${e.custom?.label ? ` · ${e.custom.label}` : ""}`,
        group: "Custom",
        custom: e.custom ?? null,
      }));
    return [...builtins, ...customs];
  }

  private addCustom(kind: HudCustomKind): void {
    const count = this.doc().elements.filter((e) => e.custom).length;
    if (count >= 24) return;
    const layout = defaultCustom(kind, undefined, count);
    this.doc().elements.push(layout);
    this.selected = layout.id;
    this.buildElements();
    this.buildProps();
    this.commit();
  }

  private deleteCustom(id: string): void {
    this.doc().elements = this.doc().elements.filter((e) => e.id !== id);
    if (this.selected === id) this.selected = null;
    this.buildElements();
    this.buildProps();
    this.commit();
  }

  /** Remove a built-in from the HUD but keep its settings for restore. */
  private deleteBuiltin(id: string): void {
    const layout = this.element(id);
    if (!layout) return;
    layout.removed = true;
    if (this.selected === id) this.selected = null;
    this.buildElements();
    this.buildProps();
    this.commit();
  }

  private restoreBuiltin(id: string): void {
    const layout = this.element(id);
    if (!layout) return;
    layout.removed = false;
    this.selected = id;
    this.buildElements();
    this.buildProps();
    this.commit();
  }

  private removedBuiltins() {
    return HUD_ELEMENTS.filter((def) => this.element(def.id)?.removed === true);
  }

  // ---- Presets (localStorage, whole playing+spectating store) -------------

  private refreshPresets(selected = ""): void {
    const list = readPresets();
    this.presetSelEl.innerHTML = "";
    const blank = el("option", "", "— presets —");
    blank.value = "";
    this.presetSelEl.append(blank);
    for (const preset of list) {
      const option = el("option", "", preset.name);
      option.value = preset.name;
      this.presetSelEl.append(option);
    }
    this.presetSelEl.value = selected && list.some((p) => p.name === selected) ? selected : "";
  }

  private savePreset(): void {
    const name = (this.presetNameEl.value.trim() || this.presetSelEl.value).slice(0, 32);
    if (!name) {
      this.presetNameEl.placeholder = "Name the preset first";
      this.presetNameEl.focus();
      return;
    }
    const list = readPresets().filter((p) => p.name !== name);
    list.push({ name, store: clone(this.store) });
    writePresets(list);
    this.presetNameEl.value = name;
    this.presetNameEl.placeholder = "Preset name";
    this.refreshPresets(name);
  }

  private loadPreset(): void {
    const name = this.presetSelEl.value;
    const preset = readPresets().find((p) => p.name === name);
    if (!preset) return;
    this.store = clone(preset.store);
    this.selected = null;
    this.buildElements();
    this.buildProps();
    this.syncDoc();
    this.commit();
  }

  private deletePreset(): void {
    const name = this.presetSelEl.value;
    if (!name) return;
    writePresets(readPresets().filter((p) => p.name !== name));
    this.refreshPresets();
  }

  private syncDoc(): void {
    this.cb.onLive(this.store, this.doc());
  }

  private commit(): void {
    this.cb.onCommit(this.store, this.doc());
  }

  private loop = (): void => {
    if (!this.open) return;
    this.drawBoxes();
    requestAnimationFrame(this.loop);
  };

  private drawBoxes(): void {
    const seen = new Set<string>();
    for (const item of this.items()) {
      const element = document.getElementById(item.id);
      if (!element) continue;
      const r = element.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) continue;
      seen.add(item.id);
      let box = this.boxMap.get(item.id);
      if (!box) {
        box = el("div", "hud-ed-box");
        box.append(el("span", "hud-ed-box-label", item.label));
        this.boxesEl.append(box);
        this.boxMap.set(item.id, box);
      }
      box.style.left = `${r.left}px`;
      box.style.top = `${r.top}px`;
      box.style.width = `${r.width}px`;
      box.style.height = `${r.height}px`;
      box.classList.toggle("selected", this.selected === item.id);
    }
    for (const [id, box] of this.boxMap) {
      if (!seen.has(id)) box.style.display = "none";
      else box.style.display = "";
    }
  }

  private hitTest(x: number, y: number): string | null {
    let best: { id: string; area: number } | null = null;
    for (const item of this.items()) {
      const e = document.getElementById(item.id);
      if (!e) continue;
      const r = e.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) continue;
      if (x < r.left || x > r.right || y < r.top || y > r.bottom) continue;
      const area = r.width * r.height;
      if (!best || area < best.area) best = { id: item.id, area };
    }
    return best?.id ?? null;
  }

  private onPointerDown(e: PointerEvent): void {
    if (e.button !== 0) return;
    const id = this.hitTest(e.clientX, e.clientY);
    if (!id) {
      this.selected = null;
      this.buildProps();
      return;
    }
    const element = document.getElementById(id);
    const rect = element?.getBoundingClientRect();
    const layout = this.element(id);
    if (!rect || !layout) return;
    const anchor = layout.anchor === "auto" ? nearestAnchor(rect) : layout.anchor;
    const wasAuto = layout.anchor === "auto";
    if (layout.anchor === "auto") {
      layout.anchor = anchor;
      layout.offset = offsetsFromRect(rect, anchor);
    }
    this.selected = id;
    this.drag = { id, startX: e.clientX, startY: e.clientY, rect, anchor, wasAuto };
    this.buildProps();
    this.buildElements();
    try {
      this.hitEl.setPointerCapture(e.pointerId);
    } catch {
      /* synthetic events (tests) have no active pointer */
    }
    this.hitEl.addEventListener("pointermove", this.onPointerMove);
    this.hitEl.addEventListener("pointerup", this.onPointerUp, { once: true });
    this.hitEl.addEventListener("pointercancel", this.onPointerUp, { once: true });
    e.preventDefault();
  }

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.drag) return;
    const layout = this.element(this.drag.id);
    if (!layout) return;
    let dx = e.clientX - this.drag.startX;
    let dy = e.clientY - this.drag.startY;
    if (e.shiftKey) {
      if (Math.abs(dx) > Math.abs(dy)) dy = 0;
      else dx = 0;
    }
    const moved = new DOMRect(
      this.drag.rect.left + dx,
      this.drag.rect.top + dy,
      this.drag.rect.width,
      this.drag.rect.height,
    );
    const off = offsetsFromRect(moved, this.drag.anchor);
    const snap = e.altKey ? 1 : 8;
    layout.offset = {
      x: Math.round(off.x / snap) * snap,
      y: Math.round(off.y / snap) * snap,
    };
    this.syncDoc();
    this.updatePropInputs();
    this.drawBoxes();
  };

  private onPointerUp = (): void => {
    const drag = this.drag;
    this.drag = null;
    this.hitEl.removeEventListener("pointermove", this.onPointerMove);
    if (this.commitTimer) {
      clearTimeout(this.commitTimer);
      this.commitTimer = 0;
    }
    if (drag) {
      const layout = this.element(drag.id);
      const rect = document.getElementById(drag.id)?.getBoundingClientRect();
      if (layout && rect) {
        // Clamp to the stage, then choose the pivot from where the element was
        // *dropped*. Choosing it from the start position left wide elements
        // centred on the edge, so the real (wider) content ran off-screen.
        const { w: vw, h: vh } = hudStageSize();
        const margin = 4;
        const left = Math.min(Math.max(rect.left, margin), Math.max(margin, vw - rect.width - margin));
        const top = Math.min(Math.max(rect.top, margin), Math.max(margin, vh - rect.height - margin));
        const clamped = new DOMRect(left, top, rect.width, rect.height);
        if (drag.wasAuto) layout.anchor = nearestAnchor(clamped);
        layout.offset = offsetsFromRect(clamped, layout.anchor);
        this.syncDoc();
        this.updatePropInputs();
        this.drawBoxes();
      }
    }
    this.commit();
  };

  /** Move the selected element by (dx, dy) px. */
  private nudge(dx: number, dy: number): void {
    const id = this.selected;
    if (!id) return;
    const layout = this.element(id);
    const rect = document.getElementById(id)?.getBoundingClientRect();
    if (!layout || !rect) return;
    if (layout.anchor === "auto") {
      layout.anchor = DEFAULT_ANCHORS[id] ?? nearestAnchor(rect);
      layout.offset = offsetsFromRect(rect, layout.anchor);
    }
    // Offsets are inward from the pivot: screen-right is negative for a
    // right-anchored element, screen-up is positive for a bottom-anchored one.
    const sx = hPart(layout.anchor) === "right" ? -1 : 1;
    const sy = vPart(layout.anchor) === "bottom" ? -1 : 1;
    layout.offset.x += dx * sx;
    layout.offset.y += dy * sy;
    this.syncDoc();
    this.updatePropInputs();
    this.drawBoxes();
    this.scheduleCommit();
  }

  /** Save shortly after the last nudge, not on every key repeat. */
  private scheduleCommit(): void {
    if (this.commitTimer) clearTimeout(this.commitTimer);
    this.commitTimer = window.setTimeout(() => {
      this.commitTimer = 0;
      this.commit();
    }, 200);
  }

  private buildElements(): void {
    this.listEl.innerHTML = "";
    const add = el("div", "hud-ed-add");
    for (const kind of ["bar", "ring", "pips", "spark"] as HudCustomKind[]) {
      const b = el("button", "xhair-btn", `＋ ${CUSTOM_KIND_LABELS[kind]}`);
      dsElement(b, "button", { style: "outline", size: "xs" });
      b.title = `Add a ${CUSTOM_KIND_LABELS[kind].toLowerCase()} value element`;
      b.addEventListener("click", () => this.addCustom(kind));
      add.append(b);
    }
    this.listEl.append(add);
    let group = "";
    for (const item of this.items()) {
      if (item.group !== group) {
        group = item.group;
        this.listEl.append(el("div", "hud-ed-group", group));
      }
      const layout = this.element(item.id);
      const row = el("div", "hud-ed-row" + (this.selected === item.id ? " active" : ""));
      const eye = el("input");
      eye.type = "checkbox";
      dsElement(eye, "toggle", { color: "primary", size: "sm" });
      eye.checked = layout?.visible !== false;
      eye.title = "visible";
      eye.addEventListener("click", (e) => e.stopPropagation());
      eye.addEventListener("change", () => {
        const l = this.element(item.id);
        if (!l) return;
        l.visible = eye.checked;
        this.commit();
      });
      const dot = el("span", "hud-ed-dot" + (layout?.color?.enabled ? " on" : ""));
      const name = el("span", "hud-ed-name", item.label);
      row.append(eye, dot, name);
      const del = el("button", "hud-ed-del", "✕");
      dsElement(del, "button", { style: "ghost", size: "xs", shape: "square", keep: "hud-ed-del" });
      del.title = item.custom ? "delete element" : "remove element";
      del.addEventListener("click", (e) => {
        e.stopPropagation();
        if (item.custom) this.deleteCustom(item.id);
        else this.deleteBuiltin(item.id);
      });
      row.append(del);
      row.addEventListener("click", () => {
        this.selected = item.id;
        this.buildElements();
        this.buildProps();
      });
      this.listEl.append(row);
    }

    const removed = this.removedBuiltins();
    if (removed.length) {
      this.listEl.append(el("div", "hud-ed-group", "Removed"));
      for (const def of removed) {
        const row = el("div", "hud-ed-row hud-ed-row-removed");
        row.append(el("span", "hud-ed-name", def.label));
        const add = el("button", "hud-ed-del hud-ed-add-back", "＋");
        dsElement(add, "button", { style: "ghost", size: "xs", shape: "square", keep: "hud-ed-del hud-ed-add-back" });
        add.title = "restore element";
        add.addEventListener("click", (e) => {
          e.stopPropagation();
          this.restoreBuiltin(def.id);
        });
        row.append(add);
        this.listEl.append(row);
      }
    }
  }

  private buildProps(): void {
    this.propsEl.innerHTML = "";
    if (!this.selected) {
      this.propsEl.append(el("div", "hud-ed-note", "Click an element to edit it (or drag it around)."));
      return;
    }
    const id = this.selected;
    const layout = this.element(id);
    if (!layout) return;
    const builtin = HUD_ELEMENT_BY_ID.get(id);
    const custom = layout.custom ?? null;
    const label = custom ? custom.label || CUSTOM_KIND_LABELS[custom.kind] : (builtin?.label ?? id);
    this.propsEl.append(el("div", "hud-ed-title", label));

    const anchorRow = el("label", "hud-ed-field");
    anchorRow.append(el("span", "", "Pivot"));
    const anchorSel = el("select");
    for (const a of ANCHOR_LABELS) {
      const o = el("option", "", a.label);
      o.value = a.value;
      anchorSel.append(o);
    }
    anchorSel.value = layout.anchor;
    anchorSel.addEventListener("change", () => {
      const a = anchorSel.value as HudAnchor;
      const rect = document.getElementById(id)?.getBoundingClientRect();
      layout.anchor = a;
      if (a === "auto") {
        layout.offset = { x: 0, y: 0 };
      } else if (rect) {
        layout.offset = offsetsFromRect(rect, a);
      }
      this.syncDoc();
      this.buildProps();
      this.commit();
    });
    anchorRow.append(anchorSel);
    this.propsEl.append(anchorRow);
    this.propsEl.append(
      el("div", "hud-ed-note", "X/Y are measured inward from the pivot. Arrow keys nudge by 1 px (Shift = 8 px)."),
    );

    this.propsEl.append(
      this.numField("X (px)", layout.offset.x, -2000, 2000, (v) => {
        if (layout.anchor === "auto") layout.anchor = DEFAULT_ANCHORS[id] ?? "center";
        layout.offset.x = v;
        this.syncDoc();
      }),
      this.numField("Y (px)", layout.offset.y, -2000, 2000, (v) => {
        if (layout.anchor === "auto") layout.anchor = DEFAULT_ANCHORS[id] ?? "center";
        layout.offset.y = v;
        this.syncDoc();
      }),
      this.rangeField("Scale", layout.scale, 0.5, 2, 0.05, (v) => {
        layout.scale = v;
        this.syncDoc();
      }),
      // The cgaz strip's alpha is owned by the cgaz style card (its inline
      // opacity is dynamic: it hides when there is no acceleration profile),
      // so the generic layout opacity is not offered here.
      ...(id === "cgaz-hud"
        ? []
        : [
            this.rangeField("Opacity", layout.opacity, 0, 1, 0.05, (v) => {
              layout.opacity = v;
              this.syncDoc();
            }),
          ]),
    );

    const zRow = el("label", "hud-ed-field");
    zRow.append(el("span", "", "Z-index (blank = auto)"));
    const zInput = el("input");
    zInput.type = "number";
    zInput.min = "-10";
    zInput.max = "100";
    zInput.value = layout.z == null ? "" : String(layout.z);
    dsElement(zInput, "input", { size: "sm", keep: "w-20" });
    zInput.addEventListener("input", () => {
      const raw = zInput.value.trim();
      layout.z = raw === "" ? null : clamp(Math.round(Number(raw) || 0), -10, 100);
      this.syncDoc();
    });
    zRow.append(zInput);
    this.propsEl.append(zRow);

    const visibleRow = el("label", "hud-ed-check");
    const vis = el("input");
    vis.type = "checkbox";
    vis.checked = layout.visible;
    dsElement(vis, "toggle", { color: "primary", size: "sm" });
    vis.addEventListener("change", () => {
      layout.visible = vis.checked;
      this.buildElements();
      this.commit();
    });
    visibleRow.append(vis, el("span", "", "Visible"));
    this.propsEl.append(visibleRow);

    const raceRow = el("label", "hud-ed-check");
    const raceOnly = el("input");
    raceOnly.type = "checkbox";
    raceOnly.checked = layout.raceOnly;
    raceOnly.title = "hidden outside race / freestyle / defrag";
    dsElement(raceOnly, "toggle", { color: "primary", size: "sm" });
    raceOnly.addEventListener("change", () => {
      layout.raceOnly = raceOnly.checked;
      this.buildElements();
      this.commit();
    });
    raceRow.append(raceOnly, el("span", "", "Race only"));
    this.propsEl.append(raceRow);

    if (custom) this.buildCustomEditor(custom);
    this.buildLookEditor(layout, custom);

    const source: HudSource | null = custom?.source ?? (builtin ? elementSource(id) : null);
    if (source) this.buildColorEditor(layout, source);

    if (custom) {
      const del = el("button", "xhair-btn", "Delete element");
      dsElement(del, "button", { style: "outline", color: "error", size: "sm" });
      del.addEventListener("click", () => this.deleteCustom(id));
      this.propsEl.append(del);
    } else {
      const reset = el("button", "xhair-btn", "Reset element");
      dsElement(reset, "button", { style: "outline", size: "sm" });
      reset.addEventListener("click", () => {
        this.doc().elements = this.doc().elements.map((e) => (e.id === id ? defaultElement(id) : e));
        this.buildElements();
        this.buildProps();
        this.commit();
      });
      this.propsEl.append(reset);
      const remove = el("button", "xhair-btn", "Remove element");
      dsElement(remove, "button", { style: "outline", color: "error", size: "sm" });
      remove.title = "Remove this element from the HUD; restore it from the Removed list";
      remove.addEventListener("click", () => this.deleteBuiltin(id));
      this.propsEl.append(remove);
    }
  }

  private buildCustomEditor(spec: HudCustomSpec): void {
    const section = el("div", "hud-ed-colors");
    section.append(el("div", "hud-ed-title", `${CUSTOM_KIND_LABELS[spec.kind]} settings`));

    const sourceRow = el("label", "hud-ed-field");
    sourceRow.append(el("span", "", "Value source"));
    const sourceSel = el("select");
    for (const src of Object.keys(SOURCE_LABELS) as HudSource[]) {
      const o = el("option", "", SOURCE_LABELS[src]);
      o.value = src;
      sourceSel.append(o);
    }
    sourceSel.value = spec.source;
    sourceSel.addEventListener("change", () => {
      spec.source = sourceSel.value as HudSource;
      spec.max = sourceMax(spec.source);
      this.syncDoc();
      this.scheduleCommit();
    });
    sourceRow.append(sourceSel);
    section.append(sourceRow);

    section.append(
      this.numField("Min", spec.min, -10000, 10000, (v) => {
        spec.min = v;
        this.syncDoc();
        this.scheduleCommit();
      }),
      this.numField("Max", spec.max, -10000, 10000, (v) => {
        spec.max = v;
        this.syncDoc();
        this.scheduleCommit();
      }),
    );

    if (spec.kind === "bar") {
      section.append(
        this.numField("Width", spec.width, 8, 800, (v) => {
          spec.width = v;
          this.syncDoc();
          this.scheduleCommit();
        }),
        this.numField("Height", spec.height, 4, 200, (v) => {
          spec.height = v;
          this.syncDoc();
          this.scheduleCommit();
        }),
        this.selectField("Direction", spec.direction, [
          ["right", "Right"],
          ["left", "Left"],
          ["up", "Up"],
          ["down", "Down"],
        ], (v) => {
          spec.direction = v as HudCustomSpec["direction"];
          this.syncDoc();
          this.scheduleCommit();
        }),
      );
    } else if (spec.kind === "ring") {
      section.append(
        this.numField("Radius", spec.radius, 4, 240, (v) => {
          spec.radius = v;
          this.syncDoc();
          this.scheduleCommit();
        }),
        this.numField("Thickness", spec.thickness, 1, 64, (v) => {
          spec.thickness = v;
          this.syncDoc();
          this.scheduleCommit();
        }),
      );
    } else if (spec.kind === "pips") {
      section.append(
        this.numField("Segments", spec.segments, 1, 64, (v) => {
          spec.segments = Math.round(v);
          this.syncDoc();
          this.scheduleCommit();
        }),
        this.numField("Width", spec.width, 8, 800, (v) => {
          spec.width = v;
          this.syncDoc();
          this.scheduleCommit();
        }),
        this.numField("Height", spec.height, 4, 200, (v) => {
          spec.height = v;
          this.syncDoc();
          this.scheduleCommit();
        }),
      );
    } else {
      section.append(
        this.numField("Width", spec.width, 20, 800, (v) => {
          spec.width = v;
          this.syncDoc();
          this.scheduleCommit();
        }),
        this.numField("Height", spec.height, 10, 300, (v) => {
          spec.height = v;
          this.syncDoc();
          this.scheduleCommit();
        }),
        this.numField("Sample ms", spec.sampleMs, 20, 2000, (v) => {
          spec.sampleMs = v;
          this.syncDoc();
          this.scheduleCommit();
        }),
      );
    }

    section.append(
      this.boolField("Show value", spec.showValue, (v) => {
        spec.showValue = v;
        this.syncDoc();
        this.buildProps();
        this.scheduleCommit();
      }),
      this.numField("Value size", spec.valueSize, 6, 96, (v) => {
        spec.valueSize = v;
        this.syncDoc();
        this.scheduleCommit();
      }),
      this.textField("Suffix", spec.suffix, (v) => {
        spec.suffix = v.slice(0, 8);
        this.syncDoc();
        this.scheduleCommit();
      }),
      this.textField("Label", spec.label, (v) => {
        spec.label = v.slice(0, 16);
        this.syncDoc();
        this.scheduleCommit();
      }),
      this.numField("Label size", spec.labelSize, 6, 48, (v) => {
        spec.labelSize = v;
        this.syncDoc();
        this.scheduleCommit();
      }),
    );
    this.propsEl.append(section);
  }

  private colorField(label: string, value: string, onChange: (hex: string) => void): HTMLElement {
    const row = el("label", "hud-ed-field");
    row.append(el("span", "", label));
    const input = el("input");
    input.type = "color";
    input.value = value;
    input.addEventListener("input", () => onChange(input.value));
    row.append(input);
    return row;
  }

  private buildLookEditor(layout: HudElementLayout, custom: HudCustomSpec | null): void {
    const look = layout.look;
    const section = el("div", "hud-ed-colors");
    section.append(el("div", "hud-ed-title", "Style"));

    section.append(
      this.boolField("Background", look.background !== null, (on) => {
        look.background = on ? "#000000" : null;
        if (on && look.backgroundOpacity <= 0) look.backgroundOpacity = 0.35;
        this.syncDoc();
        this.buildProps();
        this.scheduleCommit();
      }),
    );
    if (look.background) {
      section.append(
        this.colorField("Background colour", look.background, (hex) => {
          look.background = hex;
          this.syncDoc();
          this.scheduleCommit();
        }),
        this.rangeField("Background opacity", look.backgroundOpacity, 0, 1, 0.05, (v) => {
          look.backgroundOpacity = v;
          this.syncDoc();
        }),
      );
    }

    const hasBorder = look.borderColor !== null && look.borderWidth > 0;
    section.append(
      this.boolField("Border", hasBorder, (on) => {
        look.borderColor = on ? "#000000" : null;
        look.borderWidth = on ? Math.max(1, look.borderWidth) : 0;
        this.syncDoc();
        this.buildProps();
        this.scheduleCommit();
      }),
    );
    if (hasBorder) {
      section.append(
        this.colorField("Border colour", look.borderColor ?? "#000000", (hex) => {
          look.borderColor = hex;
          this.syncDoc();
          this.scheduleCommit();
        }),
        this.numField("Border width", look.borderWidth, 0, 8, (v) => {
          look.borderWidth = v;
          this.syncDoc();
          this.scheduleCommit();
        }),
        this.numField("Corner radius", look.borderRadius, 0, 32, (v) => {
          look.borderRadius = v;
          this.syncDoc();
          this.scheduleCommit();
        }),
      );
    }
    section.append(
      this.numField("Padding", look.padding, 0, 32, (v) => {
        look.padding = v;
        this.syncDoc();
        this.scheduleCommit();
      }),
      this.boolField("Text colour", look.textColor !== null, (on) => {
        look.textColor = on ? "#ffffff" : null;
        this.syncDoc();
        this.buildProps();
        this.scheduleCommit();
      }),
    );
    if (look.textColor) {
      section.append(
        this.colorField("Text colour value", look.textColor, (hex) => {
          look.textColor = hex;
          this.syncDoc();
          this.scheduleCommit();
        }),
      );
    }

    const hasTrack = custom !== null && custom.kind !== "spark";
    if (hasTrack) {
      section.append(
        this.boolField("Track colour", look.trackColor !== null, (on) => {
          look.trackColor = on ? "#ffffff" : null;
          this.syncDoc();
          this.buildProps();
          this.scheduleCommit();
        }),
      );
      if (look.trackColor) {
        section.append(
          this.colorField("Track value", look.trackColor, (hex) => {
            look.trackColor = hex;
            this.syncDoc();
            this.scheduleCommit();
          }),
          this.rangeField("Track opacity", look.trackOpacity, 0, 1, 0.05, (v) => {
            look.trackOpacity = v;
            this.syncDoc();
          }),
        );
      }
    }
    if (custom) {
      section.append(
        this.rangeField("Fill opacity", look.fillOpacity, 0, 1, 0.05, (v) => {
          look.fillOpacity = v;
          this.syncDoc();
        }),
      );
    }
    if (custom?.kind === "bar") {
      section.append(
        this.numField("Bar radius", look.barRadius, 0, 32, (v) => {
          look.barRadius = v;
          this.syncDoc();
          this.scheduleCommit();
        }),
      );
    }
    this.propsEl.append(section);
  }

  private selectField(
    label: string,
    value: string,
    options: [string, string][],
    onChange: (v: string) => void,
  ): HTMLElement {
    const row = el("label", "hud-ed-field");
    row.append(el("span", "", label));
    const sel = el("select");
    for (const [v, text] of options) {
      const o = el("option", "", text);
      o.value = v;
      sel.append(o);
    }
    sel.value = value;
    sel.addEventListener("change", () => onChange(sel.value));
    row.append(sel);
    return row;
  }

  private boolField(label: string, value: boolean, onChange: (v: boolean) => void): HTMLElement {
    const row = el("label", "hud-ed-check");
    const box = el("input");
    box.type = "checkbox";
    box.checked = value;
    dsElement(box, "toggle", { color: "primary", size: "sm" });
    box.addEventListener("change", () => onChange(box.checked));
    row.append(box, el("span", "", label));
    return row;
  }

  private textField(label: string, value: string, onChange: (v: string) => void): HTMLElement {
    const row = el("label", "hud-ed-field");
    row.append(el("span", "", label));
    const input = el("input");
    input.type = "text";
    input.value = value;
    input.maxLength = 16;
    dsElement(input, "input", { size: "sm", keep: "w-full" });
    input.addEventListener("input", () => onChange(input.value));
    row.append(input);
    return row;
  }

  private numField(
    label: string,
    value: number,
    min: number,
    max: number,
    onInput: (v: number) => void,
  ): HTMLElement {
    const row = el("label", "hud-ed-field");
    row.append(el("span", "", label));
    const input = el("input");
    input.type = "number";
    input.min = String(min);
    input.max = String(max);
    input.step = "1";
    input.value = String(Math.round(value));
    dsElement(input, "input", { size: "sm", keep: "w-20" });
    input.addEventListener("input", () => {
      const v = Number(input.value);
      if (Number.isFinite(v)) onInput(clamp(v, min, max));
    });
    row.append(input);
    return row;
  }

  private rangeField(
    label: string,
    value: number,
    min: number,
    max: number,
    step: number,
    onInput: (v: number) => void,
  ): HTMLElement {
    const row = el("label", "hud-ed-field");
    row.append(el("span", "", label));
    const wrap = el("div", "hud-ed-range");
    const r = el("input");
    r.type = "range";
    r.min = String(min);
    r.max = String(max);
    r.step = String(step);
    r.value = String(value);
    dsElement(r, "range", { keep: "w-full" });
    const out = el("span", "", value.toFixed(2));
    r.addEventListener("input", () => {
      const v = Number(r.value);
      out.textContent = v.toFixed(2);
      onInput(v);
    });
    wrap.append(r, out);
    row.append(wrap);
    return row;
  }

  private updatePropInputs(): void {
    // Keep the numeric X/Y fields in sync during a drag without rebuilding.
    if (!this.selected) return;
    const inputs = this.propsEl.querySelectorAll<HTMLInputElement>('input[type="number"]');
    const layout = this.element(this.selected);
    if (!layout) return;
    if (inputs[0] && document.activeElement !== inputs[0]) inputs[0].value = String(Math.round(layout.offset.x));
    if (inputs[1] && document.activeElement !== inputs[1]) inputs[1].value = String(Math.round(layout.offset.y));
  }

  private buildColorEditor(layout: HudElementLayout, source: HudSource): void {
    const section = el("div", "hud-ed-colors");
    section.append(el("div", "hud-ed-title", "Value colour"));

    const enableRow = el("label", "hud-ed-check");
    const enable = el("input");
    enable.type = "checkbox";
    enable.checked = layout.color?.enabled === true;
    dsElement(enable, "toggle", { color: "primary", size: "sm" });
    enable.addEventListener("change", () => {
      layout.color = enable.checked
        ? { enabled: true, mode: "steps", bands: clone(DEFAULT_BANDS[source]) }
        : null;
      this.buildProps();
      this.commit();
    });
    enableRow.append(enable, el("span", "", "Colour by value"));
    section.append(enableRow);

    if (!layout.color?.enabled) {
      section.append(
        el("div", "hud-ed-note", `Bands are editable for health/armor/ammo. Default: ${DEFAULT_BANDS[source].map((b) => `${b.min}+ ${b.color}`).join(" · ")}`),
      );
      this.propsEl.append(section);
      return;
    }

    const modeRow = el("label", "hud-ed-field");
    modeRow.append(el("span", "", "Mode"));
    const mode = el("select");
    for (const [value, label] of [
      ["steps", "Steps"],
      ["gradient", "Gradient"],
    ] as [string, string][]) {
      const o = el("option", "", label);
      o.value = value;
      mode.append(o);
    }
    mode.value = layout.color.mode;
    mode.addEventListener("change", () => {
      if (!layout.color) return;
      layout.color.mode = mode.value as HudValuePaint["mode"];
      this.commit();
    });
    modeRow.append(mode);
    section.append(modeRow);

    layout.color.bands.forEach((band, i) => {
      const row = el("div", "hud-ed-band");
      const min = el("input");
      min.type = "number";
      min.value = String(band.min);
      min.title = "Lowest value of this band";
      min.addEventListener("input", () => {
        band.min = Number(min.value) || 0;
        this.syncDoc();
      });
      const color = el("input");
      color.type = "color";
      color.value = band.color;
      color.addEventListener("input", () => {
        band.color = color.value;
        this.syncDoc();
      });
      row.append(el("span", "hud-ed-band-index", String(i + 1)), min, color);
      if (layout.color!.bands.length > 3) {
        const rm = el("button", "xhair-btn", "✕");
        dsElement(rm, "button", { style: "ghost", size: "xs", shape: "square" });
        rm.title = "remove band";
        rm.addEventListener("click", () => {
          layout.color!.bands.splice(i, 1);
          this.buildProps();
          this.commit();
        });
        row.append(rm);
      }
      section.append(row);
    });

    if (layout.color.bands.length < 5) {
      const add = el("button", "xhair-btn", "+ band");
      dsElement(add, "button", { style: "outline", size: "xs" });
      add.addEventListener("click", () => {
        if (!layout.color) return;
        const last = layout.color.bands[layout.color.bands.length - 1];
        layout.color.bands.push({ min: last.min + 25, color: isHex(last.color) ? last.color : "#ffffff" });
        this.buildProps();
        this.commit();
      });
      section.append(add);
    }

    this.propsEl.append(section);
  }

  private buildTestValues(): void {
    this.testEl.innerHTML = "";
    this.testEl.append(el("div", "hud-ed-title", "Test values"));
    for (const [source, max] of [
      ["health", 200],
      ["armor", 200],
      ["ammo", 100],
      ["speed", 5000],
    ] as ["health" | "armor" | "ammo" | "speed", number][]) {
      const row = el("label", "hud-ed-field");
      row.append(el("span", "", source));
      const wrap = el("div", "hud-ed-range");
      const r = el("input");
      r.type = "range";
      r.min = "0";
      r.max = String(max);
      r.step = "1";
      r.value = String(this.testValues[source]);
      dsElement(r, "range", { keep: "w-full" });
      const out = el("span", "", String(this.testValues[source]));
      r.addEventListener("input", () => {
        const v = Number(r.value);
        this.testValues[source] = v;
        out.textContent = String(v);
        this.cb.onTestValue(source, v);
      });
      wrap.append(r, out);
      row.append(wrap);
      this.testEl.append(row);
    }
  }

  private applyPreviewText(): void {
    for (const [id, text] of Object.entries(PREVIEW)) {
      const e = document.getElementById(id);
      if (!e) continue;
      if (!e.textContent?.trim()) {
        this.previewOriginals.set(id, e.textContent ?? "");
        e.textContent = text;
      }
    }
    const match = [
      ["match-phase", "WARMUP"],
      ["match-score", "0 — 0"],
      ["match-timer", "10:00"],
    ] as [string, string][];
    for (const [id, text] of match) {
      const e = document.getElementById(id);
      if (e && !e.textContent?.trim()) {
        this.previewOriginals.set(id, e.textContent ?? "");
        e.textContent = text;
      }
    }
  }

  private restorePreviewText(): void {
    for (const [id, text] of this.previewOriginals) {
      const e = document.getElementById(id);
      if (e) e.textContent = text;
    }
    this.previewOriginals.clear();
  }
}

// Referenced by the menu for a quick reset from Settings → HUD.
export function resetHudStore(store: HudLayoutStore): HudLayoutStore {
  return { ...defaultStore(), spectateUsePlayer: store.spectateUsePlayer };
}
